'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';

import { NotFoundContent } from '@/src/app/_components/NotFoundContent';
import { useHeaderVisibility } from '@/src/layouts/HeaderVisibilityContext';
import { usePathname } from '@/src/i18n/navigation';

const LOCALE_SEGMENTS = new Set(['en', 'es']);
const HDP_SUFFIXES = new Set(['pad', 'building']);

function toTitleCase(value: string): string {
  return value
    .split(' ')
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`)
    .join(' ');
}

export function getHdpRemovedListingAddress(pathname: string): string | null {
  const pathnameSegments = pathname.split('/').filter(Boolean);
  const routeSegments = LOCALE_SEGMENTS.has(pathnameSegments[0] ?? '') ? pathnameSegments.slice(1) : pathnameSegments;
  const suffix = routeSegments.at(-1);
  const listingSlug = routeSegments[0];

  if (!listingSlug || !suffix || !HDP_SUFFIXES.has(suffix)) return null;

  const address = listingSlug.split('-').slice(0, -1).join(' ');
  if (!address) return null;

  const unit = suffix === 'pad' && routeSegments.length === 3 ? routeSegments[1] : null;
  return unit ? `${toTitleCase(address)}, ${toTitleCase(unit)}` : toTitleCase(address);
}

export function HdpNotFoundContent() {
  const t = useTranslations('notFound');
  const pathname = usePathname() ?? '';
  const address = getHdpRemovedListingAddress(pathname) ?? t('removedListingFallbackAddress');
  const { setHideHeaderOnMobile } = useHeaderVisibility();

  // Direct HDP loads seed the header hidden (CLS); a dead listing renders this
  // 404 with the normal chrome instead, so restore the header.
  useEffect(() => {
    setHideHeaderOnMobile(false);
  }, [setHideHeaderOnMobile]);

  return (
    <NotFoundContent
      headingText={t('removedListingHeading', { address })}
      hideArtwork
      subtext={t('removedListingDescription')}
    />
  );
}
